import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useInView } from 'react-intersection-observer'
import { Box, BoxProps, LazyList, Paragraph, Stack, TextField } from '@ui'
import fuzzysort from 'fuzzysort'
import { useDevice } from 'hooks/useDevice'
import { useThrottledValue } from 'hooks/useThrottledValue'
import { isEqualWith } from 'lodash-es'

/** lazy display starts above this threshold */
const LAZY_OPTIONS_THRESHOLD = 10
const LAZY_OPTIONS_PAGE_SIZE = 50

type Props<T> = {
    emptySelectionElement?: (params: {
        searchTerm: string
        onAddItem: (customKey: string) => void
    }) => React.JSX.Element
    getOptionKey: (option: T) => string
    keys: string[]
    label?: string | React.JSX.Element
    cta?: React.JSX.Element
    onConfirm?: () => void
    onPreviewChange?: (previewItem: T | undefined) => void
    onSelectionChange?: (selection: Set<string>) => void
    searchItemsRenderer?: (searchItems: T[]) => React.JSX.Element
    searchFunction?: (searchTerm: string, options: T[]) => T[]
    optionRenderer: (props: {
        option: T
        selected: boolean
        onAddItem: (customKey?: string) => void
    }) => React.JSX.Element
    optionPlaceholder?: React.JSX.Element
    options: T[]

    pillRenderer: (params: {
        key: string
        onDelete: (customKey?: string) => void
        selection: Set<string>
    }) => React.JSX.Element
    placeholder?: string
    initialFocusIndex?: number
    initialSelection?: Set<string>
    onBeforeOptionAdded?: (option: T) => false | void
    /**
     * Pass a function to transform the selection before rendering the pills
     * A hack for manipulating the selection strings to shove in extra data, later we should allow selection to be whatever we want
     */
    transformSelectionForPillRendering?: (selection: Set<string>) => Set<string>
    /**
     * Hides the initial search items until the input is focused
     */
    hideResultsWhenNotActive?: boolean
    autoFocus?: boolean
    inputContainerRef?: React.RefObject<HTMLDivElement>
    isError?: boolean
    fieldRefOverride?: React.RefObject<HTMLInputElement>
    // default to "card" override to "none" to remove the shadow
    boxShadow?: BoxProps['boxShadow']

    // default to "default" override to 'none' to remove the border
    border?: BoxProps['border']
}

export const PillSelector = <T,>(props: Props<T>) => {
    const { isTouch } = useDevice()

    const {
        options,
        keys,
        getOptionKey,
        optionRenderer,
        pillRenderer,
        placeholder,
        initialFocusIndex = isTouch ? -1 : 0,
        transformSelectionForPillRendering,
        hideResultsWhenNotActive,
        autoFocus = true,
        inputContainerRef,
        searchItemsRenderer,
        initialSelection,
        isError,
        fieldRefOverride,
        onBeforeOptionAdded,
        boxShadow = 'card',
        border = 'default',
    } = props

    const containerRef = useRef<HTMLDivElement>(null)

    // search field
    const _fieldRef = useRef<HTMLInputElement>(null)
    const fieldRef = fieldRefOverride ?? _fieldRef

    // search results container
    const listRef = useRef<HTMLDivElement>(null)

    const [inputValue, setInputValue] = useState<string>('')
    const [searchTerm, setSearchTerm] = useState<string>('')

    const debouncedSearchTerm = useThrottledValue(searchTerm, 100)
    const [focusIndex, setFocusIndex] = useState(initialFocusIndex)
    const [selection, setSelection] = useState<Set<string>>(() => initialSelection ?? new Set())
    const [isInsideContainer, setIsInsideContainer] = useState(false)

    const defaultSearchFunction = useCallback(
        (searchTerm: string, options: T[]) => {
            return fuzzysort.go(searchTerm, options, { keys, all: true }).map((r) => r.obj)
        },
        [keys],
    )

    const searchFunctionRef =
        useRef<(searchTerm: string, options: T[]) => T[]>(defaultSearchFunction)
    searchFunctionRef.current = props.searchFunction ?? defaultSearchFunction

    // -------------------------------------------------------------------------

    const prevSearchItems = useRef<T[]>([])

    const selectedKeysSet = useMemo(() => new Set(Array.from(selection)), [selection])

    const searchItems = useMemo(() => {
        // default behavior is to show suggested before typing
        // once a selection is made, suggestions are shown once user starts typing
        if (!debouncedSearchTerm && selection.size > (isTouch ? 1 : 0)) {
            return []
        }

        // hideResultsWhenNotActive hides the initial suggestion until input is focused
        if (hideResultsWhenNotActive && !isInsideContainer) {
            return []
        }

        const results = searchFunctionRef
            .current(debouncedSearchTerm, options)
            // prevent showing already selected options - use memoized set for better performance
            .filter((f) => !selectedKeysSet.has(getOptionKey(f)))

        // Use shallow comparison for better performance when possible
        if (results.length === prevSearchItems.current.length) {
            const isSame = results.every(
                (item, index) =>
                    getOptionKey(item) === getOptionKey(prevSearchItems.current[index]),
            )
            if (isSame) {
                return prevSearchItems.current
            }
        }

        return results
    }, [
        debouncedSearchTerm,
        selection.size,
        isTouch,
        hideResultsWhenNotActive,
        isInsideContainer,
        options,
        selectedKeysSet,
        getOptionKey,
    ])

    useEffect(() => {
        const handleFocus = (event: FocusEvent) => {
            if (containerRef.current?.contains(event.target as Node | null)) {
                setIsInsideContainer(true)
            } else {
                setIsInsideContainer(false)
            }
        }

        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current?.contains(event.target as Node | null)) {
                setIsInsideContainer(true)
            } else {
                setIsInsideContainer(false)
            }
        }

        document.addEventListener('focus', handleFocus, true)
        document.addEventListener('click', handleClickOutside, true)

        return () => {
            document.removeEventListener('focus', handleFocus, true)
            document.removeEventListener('click', handleClickOutside, true)
        }
    }, [])

    const { onPreviewChange } = props
    const onPreviewChangeRef = useRef(onPreviewChange)
    onPreviewChangeRef.current = onPreviewChange

    useEffect(() => {
        const previewItem = searchItems[focusIndex]
        onPreviewChangeRef.current?.(previewItem)
    }, [focusIndex, searchItems])

    prevSearchItems.current = searchItems

    const searchItemsRef = useRef<T[]>(searchItems)
    useEffect(() => {
        if (
            isEqualWith(
                searchItems,
                searchItemsRef.current,
                (a, b) => getOptionKey(a) === getOptionKey(b),
            )
        ) {
            return
        }
        searchItemsRef.current = searchItems
        setFocusIndex(initialFocusIndex)
    }, [getOptionKey, initialFocusIndex, searchItems])

    const onAddItem = useCallback(
        (key: string) => {
            if (onBeforeOptionAdded?.(options.find((o) => getOptionKey(o) === key)!) !== false) {
                setSelection((s) => new Set(s.add(key)))
            }
        },
        [getOptionKey, options, onBeforeOptionAdded],
    )

    const onDeleteItem = useCallback((key: string) => {
        setSelection((s) => {
            s.delete(key)
            return new Set(s)
        })
    }, [])

    // -------------------------------------------------------------------------

    const onChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        // Update input value immediately for controlled component responsiveness
        setInputValue(value)
        // Update search term in transition for search functionality
        React.startTransition(() => {
            setSearchTerm(value)
        })
    }, [])

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            const currentInputValue = inputValue

            switch (e.key) {
                case 'Tab':
                case 'Enter': {
                    if (e.shiftKey || e.altKey || e.metaKey) {
                        break
                    }
                    e.preventDefault()

                    const el = listRef.current?.children[focusIndex] as HTMLDivElement
                    if (el) {
                        e.stopPropagation()
                        // grab the first click handler inside the optionRederer
                        for (let i = 0; i < el.children.length; i++) {
                            const child = el.children[i] as HTMLButtonElement
                            if (child.onclick != null) {
                                child.click()
                                break
                            }
                        }
                    } else {
                        props.onConfirm?.()
                    }
                    break
                }
                case 'ArrowUp': {
                    e.preventDefault()
                    setFocusIndex((a) => Math.max(0, a - 1))
                    break
                }
                case 'ArrowDown': {
                    e.preventDefault()
                    setFocusIndex((a) => Math.min(searchItems.length - 1, a + 1))
                    break
                }
                case 'Backspace': {
                    if (!currentInputValue) {
                        e.preventDefault()
                        setSelection((s) => new Set(Array.from(s).slice(0, -1)))
                    }
                    break
                }
            }
        },
        [focusIndex, props, searchItems.length, inputValue],
    )

    // -------------------------------------------------------------------------

    useEffect(() => {
        listRef.current?.children[focusIndex]?.scrollIntoView({
            block: 'center',
        })
    }, [focusIndex])

    useEffect(() => {
        if (selection.size > 0) {
            fieldRef.current?.focus()
        }
    }, [fieldRef, selection])

    // -------------------------------------------------------------------------

    const onSelectionChangeRef = useRef<Props<T>['onSelectionChange'] | undefined>(undefined)
    onSelectionChangeRef.current = props.onSelectionChange

    useLayoutEffect(() => {
        if (selection) {
            setInputValue('')
            setSearchTerm('')
            setFocusIndex(initialFocusIndex)
        }
        onSelectionChangeRef.current?.(selection)
    }, [initialFocusIndex, selection])

    // -------------------------------------------------------------------------

    return (
        <Stack gap="sm" ref={containerRef}>
            {/* input container */}
            <Box
                horizontal
                ref={inputContainerRef}
                paddingX="md"
                paddingY="sm"
                gap="sm"
                background="level2"
                rounded="sm"
                flexWrap="wrap"
                minHeight="x6"
                boxShadow={boxShadow}
                overflow="hidden"
                border={isError ? 'negative' : border}
                onClick={() => {
                    fieldRef?.current?.focus()
                }}
            >
                {Array.from(transformSelectionForPillRendering?.(selection) ?? selection).map(
                    (key) => (
                        <Box horizontal key={key}>
                            {pillRenderer({
                                key,
                                selection,
                                onDelete: (customKey?: string) => {
                                    onDeleteItem(customKey ?? key)
                                },
                            })}
                        </Box>
                    ),
                )}
                <TextField
                    grow
                    data-testid="pill-selector-input"
                    autoFocus={autoFocus}
                    ref={fieldRef}
                    tone="none"
                    value={inputValue}
                    placeholder={placeholder}
                    paddingX="none"
                    paddingY="none"
                    height="x2"
                    size={Math.max(3, inputValue.length + 1)}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                />
            </Box>
            {/* dropdown */}
            {searchItems?.length > 0 || props.cta ? (
                <Box
                    padding
                    gap
                    scroll
                    rounded="sm"
                    background="level2"
                    style={{ maxHeight: 380 }}
                    boxShadow="card"
                    zIndex="above"
                >
                    {props.cta}
                    {/* label */}
                    {searchItems.length === 0 ? null : typeof props.label === 'string' ? (
                        <Box>
                            <Paragraph color="gray2">{props.label}</Paragraph>
                        </Box>
                    ) : (
                        props.label
                    )}
                    {/* list container*/}
                    {searchItems.length > 0 && searchItemsRenderer ? (
                        searchItemsRenderer(searchItems)
                    ) : (
                        <Stack gap="sm" ref={listRef} data-testid="suggested-people-list-entries">
                            <LazyList
                                items={searchItems}
                                pageSize={LAZY_OPTIONS_PAGE_SIZE}
                                mapItems={(o, i) => {
                                    const view = () => {
                                        return optionRenderer({
                                            option: o,
                                            selected: focusIndex === i,
                                            onAddItem: (customKey?: string) =>
                                                onAddItem(customKey ?? getOptionKey(o)),
                                        })
                                    }
                                    return props.optionPlaceholder && i > LAZY_OPTIONS_THRESHOLD ? (
                                        <LazyOptionRenderer
                                            key={getOptionKey(o)}
                                            placeholder={props.optionPlaceholder}
                                        >
                                            {view}
                                        </LazyOptionRenderer>
                                    ) : (
                                        <Box key={getOptionKey(o)}>{view()}</Box>
                                    )
                                }}
                            />
                        </Stack>
                    )}
                </Box>
            ) : hideResultsWhenNotActive && !isInsideContainer ? null : (
                <Box zIndex="above">
                    {props.emptySelectionElement?.({
                        searchTerm,
                        onAddItem: (customKey: string) => onAddItem(customKey),
                    })}
                </Box>
            )}
        </Stack>
    )
}

const LazyOptionRenderer = React.memo(
    (props: { children: () => React.ReactNode; placeholder: React.JSX.Element }) => {
        const { inView, ref } = useInView({ rootMargin: '500px', delay: 100 })
        return <div ref={ref}>{inView ? props.children() : props.placeholder}</div>
    },
)
